import * as React from 'react'
import {createRoot} from 'react-dom/client'

import {Text} from '@ua/design-system/components/text'
import FlightDeckChakraProvider from '@ua/design-system/FlightDeckChakraProvider'

import {Features} from 'airship/clients/feature-loader/types'
import ExperimentalFeatureSettings from 'airship/components/AppNav/BEES/ExperimentalFeatureSettings'
import FeatureLoader from 'airship/components/FeatureLoader'
import PortalPool, {
  ROOT_POOL_ID,
} from 'airship/components/RactivePortal/PortalPool'
import NewPushPopover from 'airship/widgets/new-push-popover'

import HelpMenu from '../components/HelpMenu/HelpMenu'
import AirshipProvider from './AirshipProvider'
import NavBar from './AppNavBar'

type Module = () => void | React.ReactElement

const Wrapper: React.FC<{m: Module}> = ({m}) => {
  const instance = m()
  if (
    instance &&
    (React.isValidElement(instance) || isReactElement(instance))
  ) {
    return instance
  }
  return <></>
}

export default function loadApp(modules: Module[]): void {
  const airshipReactRootContainer =
    document.getElementById('airship-react-root')
  const flightDeckContainer = document.getElementById('flight-deck')
  // This condition is used to only apply color mode to Flight Deck.
  // Otherwise, we would apply color mode to all pages and we don't want that.
  if (flightDeckContainer) {
    const flightDeckRoot = createRoot(flightDeckContainer)
    flightDeckRoot.render(
      <FlightDeckChakraProvider>
        {modules.map((module, idx) => {
          return <Wrapper m={module} key={idx} />
        })}
      </FlightDeckChakraProvider>
    )
  } else if (airshipReactRootContainer) {
    const airshipReactRoot = createRoot(airshipReactRootContainer)
    airshipReactRoot.render(
      <AirshipProvider>
        {modules.map((module, idx) => {
          return <Wrapper m={module} key={idx} />
        })}
        <PortalPool poolId={ROOT_POOL_ID} />
      </AirshipProvider>
    )
  }
  const globalNavBarContainer = document.querySelector(
    '[data-testid="global-navbar-container"]'
  )

  const helpMenu = document.querySelector('[data-testid="help-menu"]')
  const newPushPopoverContainer = document.querySelector(
    '.create-new-push-button'
  )

  if (globalNavBarContainer) {
    const globalNavBarRoot = createRoot(globalNavBarContainer)
    globalNavBarRoot.render(
      <AirshipProvider>
        <NavBar />
      </AirshipProvider>
    )
  }
  if (helpMenu) {
    const helpMenuRoot = createRoot(helpMenu)
    helpMenuRoot.render(
      <AirshipProvider>
        <HelpMenu />
      </AirshipProvider>
    )
  }
  if (newPushPopoverContainer) {
    const newPushPopoverRoot = createRoot(newPushPopoverContainer)
    newPushPopoverRoot.render(
      <AirshipProvider>
        <FeatureLoader
          resolve={[
            Features.ContentAuthor,
            Features.PushComposer,
            Features.Experiments,
            Features.AppleNewsComposer,
            Features.JourneysRead,
            Features.TriggersTag,
            Features.InAppAutomationComposer,
            Features.ContentTemplates,
            Features.Survey,
            Features.Scene,
            Features.WebScene,
          ]}
          render={(features) => {
            return (
              <NewPushPopover
                hasContentAuthorAccess={features[Features.ContentAuthor]}
                hasPComposerAccess={features[Features.PushComposer]}
                hasExperimentsAccess={features[Features.Experiments]}
                hasAppleNews={features[Features.AppleNewsComposer]}
                hasJourneys={features[Features.JourneysRead]}
                hasTriggersTag={features[Features.TriggersTag]}
                hasInAppAutomation={features[Features.InAppAutomationComposer]}
                hasTemplates={features[Features.ContentTemplates]}
                hasSurvey={features[Features.Survey]}
                hasScene={features[Features.Scene]}
                hasWebScene={features[Features.WebScene]}
              />
            )
          }}
          errorDisplay={<Text>Error</Text>}
          loadingDisplay={<></>}
        />
      </AirshipProvider>
    )
  }
  const experimentalFeaturesButton = document.querySelector(
    '#experimental-features-button'
  )
  if (experimentalFeaturesButton) {
    const experimentalFeaturesButtonRoot = createRoot(
      experimentalFeaturesButton
    )
    experimentalFeaturesButtonRoot.render(
      <AirshipProvider>
        <FeatureLoader
          resolve={[Features.Bees]}
          render={(features) => {
            return (
              <ExperimentalFeatureSettings
                hasBeesToggle={features[Features.Bees]}
              />
            )
          }}
        />
      </AirshipProvider>
    )
  }
}

function isReactElement(obj: unknown): obj is React.ReactElement {
  return (
    Object.prototype.hasOwnProperty.call(obj, 'children') &&
    Object.prototype.hasOwnProperty.call(obj, 'containerInfo')
  )
}
